/* Lotus Wheel savings calculator. Live recalc (massages × cost × 12 × years
   − wheel price). Sliders sit at their defaults (no entry motion). Only the
   big "You'll save" total animates: it counts up when scrolled into view and
   tweens whenever a slider moves. Progressive enhancement; reduced-motion
   shows the final value with no count-up. */
class SavingsCalc extends HTMLElement {
  connectedCallback() {
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.wheelPrice = parseFloat(this.dataset.wheelPrice) || 199;

    this.sliders = Array.from(this.querySelectorAll('.calc__range')).map((input) => ({
      input,
      key: input.dataset.calcInput,
      prefix: input.dataset.calcPrefix || '',
      min: parseFloat(input.min),
      max: parseFloat(input.max),
      step: parseFloat(input.step) || 1,
      display: input.closest('.calc__slider').querySelector('[data-calc-display]'),
    }));

    this.saveEl = this.querySelector('[data-calc-save]');
    this.massEl = this.querySelector('[data-calc-massages]');
    this.wheelEl = this.querySelector('[data-calc-wheel]');
    this.wheelBar = this.querySelector('[data-calc-wheel-bar]');
    this.priceEls = Array.from(this.querySelectorAll('[data-calc-price]'));
    this.yearsEls = Array.from(this.querySelectorAll('[data-calc-years]'));
    this.currentSave = 0;

    this.priceEls.forEach((el) => (el.textContent = this.money(this.wheelPrice)));
    if (this.wheelEl) this.wheelEl.textContent = this.money(this.wheelPrice);

    this.sliders.forEach((s) => {
      this.paintFill(s, parseFloat(s.input.value));
      this.setDisplay(s, parseFloat(s.input.value));
      s.input.addEventListener('input', () => {
        this.paintFill(s, parseFloat(s.input.value));
        this.setDisplay(s, parseFloat(s.input.value));
        this.recalc(true);
      });
    });

    // Set everything to its final value first (sliders already in place).
    this.recalc(false);

    if (this.reduced) return;

    // Re-arm only the big number to 0, then count it up when in view.
    this.currentSave = 0;
    if (this.saveEl) this.saveEl.textContent = this.money(0);
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.recalc(true);
          this.observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    this.observer.observe(this);
  }

  money(n) { return '$' + Math.round(n).toLocaleString(); }
  roundStep(v, step) { return Math.round(v / step) * step; }

  setDisplay(s, val) {
    if (s.display) s.display.textContent = s.prefix + this.roundStep(val, s.step).toLocaleString();
  }

  paintFill(s, val) {
    const pct = ((val - s.min) / (s.max - s.min)) * 100;
    s.input.style.setProperty('--calc-pct', pct + '%');
  }

  values() {
    const v = {};
    this.sliders.forEach((s) => {
      v[s.key] = this.roundStep(parseFloat(s.input.value), s.step);
    });
    return v;
  }

  recalc(animate) {
    const v = this.values();
    const massTotal = (v.massages || 0) * (v.cost || 0) * 12 * (v.years || 0);
    const save = Math.max(0, massTotal - this.wheelPrice);

    this.yearsEls.forEach((el) => (el.textContent = v.years));
    if (this.massEl) this.massEl.textContent = this.money(massTotal);
    if (this.wheelBar) {
      const wpct = Math.max(1.5, (this.wheelPrice / Math.max(massTotal, 1)) * 100);
      this.wheelBar.style.width = Math.min(100, wpct) + '%';
    }

    if (animate) {
      this.tween(this.saveEl, this.currentSave, save, 600);
    } else if (this.saveEl) {
      this.saveEl.textContent = this.money(save);
    }
    this.currentSave = save;
  }

  tween(el, from, to, dur) {
    if (!el) return;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = this.money(from + (to - from) * eased);
      if (p < 1) requestAnimationFrame(tick);
      else el.textContent = this.money(to);
    };
    requestAnimationFrame(tick);
  }

  disconnectedCallback() { this.observer?.disconnect(); }
}

customElements.define('savings-calc', SavingsCalc);
